import { type ChangeEvent, useState } from 'react';

import { uploadProductImage } from '../../data/mutators';
import { prepareProductImage } from '../../lib/productImage';

/**
 * Picks, downsizes and uploads a product photo. The upload only stores the
 * file; the URL is linked to the product when the form is saved, so leaving
 * the form without saving keeps the old photo.
 */
export function ProductImageField({
  imageUrl,
  productName,
  disabled,
  ensureProductId,
  onChange,
}: {
  imageUrl?: string;
  productName: string;
  disabled: boolean;
  /** Returns the product id, assigning one first for a new product. */
  ensureProductId: () => string;
  onChange: (imageUrl: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = '';
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const image = await prepareProductImage(file);
      const result = await uploadProductImage(ensureProductId(), image);
      onChange(result.imageUrl);
    } catch (cause) {
      // ProductImageError and API errors both carry a Thai, user-facing message.
      setError(cause instanceof Error ? cause.message : 'อัปโหลดรูปไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  };

  const locked = disabled || uploading;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm-label font-semibold text-ink-2">รูปสินค้า</span>
      <div
        aria-busy={uploading}
        className="relative flex aspect-square w-36 items-center justify-center overflow-hidden rounded-card border border-line bg-surface-2"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={productName ? `รูป ${productName}` : 'รูปสินค้า'}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex flex-col items-center gap-1 text-xs text-ink-3">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="h-7 w-7 text-ink-4"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            ยังไม่มีรูป
          </span>
        )}
        {uploading && (
          <span
            role="status"
            className="absolute inset-0 flex items-center justify-center bg-surface/80 text-xs font-medium text-ink"
          >
            กำลังอัปโหลด…
          </span>
        )}
      </div>
      {!disabled && (
        <div className="flex w-36 gap-1">
          <label
            className={`inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center rounded-btn border border-line bg-surface px-2 text-sm-label font-medium text-ink transition-colors hover:border-control hover:bg-bg-warm focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink ${
              locked ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            {imageUrl ? 'เปลี่ยน' : 'เลือกรูป'}
            <input
              type="file"
              accept="image/*"
              aria-label="อัปโหลดรูปสินค้า"
              disabled={locked}
              onChange={(event) => void onPick(event)}
              className="sr-only"
            />
          </label>
          {imageUrl && (
            <button
              type="button"
              disabled={locked}
              onClick={() => {
                setError('');
                onChange('');
              }}
              className="min-h-11 rounded-btn px-2 text-sm-label font-medium text-ink-3 transition-colors hover:bg-danger-light hover:text-danger disabled:opacity-50"
            >
              ลบรูป
            </button>
          )}
        </div>
      )}
      {error ? (
        <span role="alert" className="w-36 text-xs text-danger">
          {error}
        </span>
      ) : null}
    </div>
  );
}
